import queryString from 'query-string';
import { useCallback } from 'react';

import { Box } from 'components/Box';
import { H3 } from 'components/Text/H3';
import { CmsApiTileGridBlockData } from 'modules/cms/api/types/blocks/CmsApiTileGridBlock';
import { CmsAmpCardsBlock } from 'modules/cms/blocks/Amp/Cards';
import { CmsAmpBlockMappingComponentProps } from 'modules/cms/blocks/CmsBlockMapping';
import { getContentPageUrl } from 'modules/cms/helpers';

import { getCmsTileGridSectionQueryParamName } from './helpers/getCmsTileGridSectionQueryParamName';
import { getCmsTileGridTileQueryParamName } from './helpers/getCmsTileGridTileQueryParamName';
import {
  IdentifiedTileGridBlockSection,
  IdentifiedTileGridBlockTile,
  useIdentifiedTileGridBlockData,
} from './hooks/useIdentifiedTileGridBlockData';

export function CmsAmpTileGridBlock({
  content,
  data,
  blockId,
}: CmsAmpBlockMappingComponentProps<CmsApiTileGridBlockData>) {
  const { sections, imageSize } = data;
  const { identifiedSections } = useIdentifiedTileGridBlockData({
    blockId,
    sections,
  });

  const identifiedTileGridTileToAmpCard = useCallback(
    (
      identifiedTile: IdentifiedTileGridBlockTile,
      identifiedSection: IdentifiedTileGridBlockSection,
    ) => {
      const pageUrl = getContentPageUrl({ content });
      const text = `${identifiedTile.description.slice(0, 120)}${
        identifiedTile.description.length > 120 ? '...' : ''
      }`;

      const tileQueryParam = getCmsTileGridTileQueryParamName({ blockId });
      const sectionQueryParam = getCmsTileGridSectionQueryParamName({
        blockId,
      });
      const queryParams = queryString.stringify({
        [tileQueryParam]: identifiedTile.tileSlug,
        [sectionQueryParam]: identifiedSection.sectionSlug,
      });

      const url = `${pageUrl}?${queryParams}`;

      return {
        image: identifiedTile.thumbnail,
        headline: identifiedTile.heading,
        subheadline: identifiedTile.subHeading,
        text,
        url,
      };
    },
    [blockId, content],
  );

  return (
    <>
      {identifiedSections.map((identifiedSection, index) => (
        <Box mt={index > 0 ? 48 : undefined}>
          <H3>{identifiedSection.name}</H3>

          <CmsAmpCardsBlock
            content={content}
            blockId={blockId}
            data={{
              imageSize,
              cards: identifiedSection.identifiedTiles.map((identifiedTile) =>
                identifiedTileGridTileToAmpCard(
                  identifiedTile,
                  identifiedSection,
                ),
              ),
            }}
          />
        </Box>
      ))}
    </>
  );
}
